import React from 'react';
import moment from 'moment';
import classnames from 'classnames';
import { Button, message } from 'antd';
import noop from 'lodash/noop';
import { ModalMask } from '@components/common';
import { uuidv4 } from '@utils';
import Calendar from './Calendar';
import Display from './Display';
import Shortcuts from './Shortcuts';
import IntervalSelector from './IntervalSelector';
import styles from './styles/DatePicker.less';
import { getFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';

function isMGWSamplingCutoverInInterval(intervalStart, intervalEnd) {
  const mgwSamplingCutoverDate = getFeature(featureNames.MGW_SAMPLING_CUTOVER_DATE);
  const cutoverDateParsedAsUTC = moment.utc(mgwSamplingCutoverDate);

  if (cutoverDateParsedAsUTC.isValid()) {
    const DATE_RANGE_INCLUDES_SAMPLING_CUTOVER_MSG = `Please select a date range that is entirely before or after ${moment(cutoverDateParsedAsUTC.toISOString()).format('YYYY-MM-DD HH:mm')}`;

    if (cutoverDateParsedAsUTC?.isBetween(intervalStart, intervalEnd)) {
      message.error(DATE_RANGE_INCLUDES_SAMPLING_CUTOVER_MSG, 5);
      return true;
    }
  }
  return false;
}

const CUSTOM = 'custom';

class DatePicker extends React.PureComponent {
  constructor(props) {
    super(props);
    const { defaultValue, defaultShortcut, granularity } = props;

    this.state = {
      interval: defaultValue,
      selectedShortcut: defaultShortcut,
      open: false,
    };
    this.display = React.createRef();
    this.modalId = uuidv4();
  }

  componentDidMount() {
    this.display.current.addEventListener('click', this.toggleOpen);
    document.addEventListener('mousedown', this.clickModalBg);
  }

  componentWillUnmount() {
    this.display.current.removeEventListener('click', this.toggleOpen);
    document.removeEventListener('mousedown', this.clickModalBg);
  }

  isModelOpen = () => {
    return this.state.open;
  };

  clickModalBg = (e) => {
    const { onCancel } = this.props;
    if (e.target.id === this.modalId) {
      onCancel();
      setTimeout(() => {
        this.toggleOpen();
      }, 0);
    }
  };

  setDate = (dates) => {
    this.setState({
      interval: dates,
      selectedShortcut: CUSTOM,
    });
  };

  toggleOpen = () => {
    const { defaultValue, defaultShortcut, onClose, onOpen } = this.props;
    const { open } = this.state;
    if (open) {
      onClose();
      this.setState({
        open: false,
        interval: defaultValue,
        selectedShortcut: defaultShortcut,
      });
    } else {
      onOpen();
      this.setState({ open: true });
    }
  };

  close = () => {
    const { onClose } = this.props;
    onClose();
    this.setState({
      open: false,
    });
  };

  reset = () => {
    const { defaultValue, defaultShortcut } = this.props;
    this.setState({
      interval: defaultValue,
      selectedShortcut: defaultShortcut,
    });
  };

  onApply = () => {
    const { interval, selectedShortcut } = this.state;
    const { onApply, onClose } = this.props;

    if (isMGWSamplingCutoverInInterval(interval[0], interval[1])) {
      return;
    }

    this.setState({
      open: false,
    });
    onApply(interval, selectedShortcut);
    onClose();
  };

  updateDate = (interval) => {
    const { onChange, fixedRange } = this.props;
    const [from, to] = interval;
    let newInterval;

    // both onChange and onCalendarChange are triggered for each click
    // for compare dod picker, set fixed range based on start value
    if (fixedRange === false) {
      /* CE-7280: change the 'to' to endOf minute to change the interval end
       IF server now adds a padding of 10 seconds rather than 1 minute
       as a workaround for end interval being exclusive in the Druid.
       'to' before change : 23:59:00
       'to' after change : 23:59:59
      */
      const isToday = to.isSame(moment(), 'day');
      const toTime = isToday ? moment() : to.endOf('day').endOf('minute');
      newInterval = [from.startOf('day'), toTime];
    } else {
      newInterval = this.formatCompareInterval(interval, fixedRange);
    }

    onChange({ interval: newInterval, selectedShortcut: CUSTOM });
    this.setState({
      interval: newInterval,
      selectedShortcut: CUSTOM,
    });
  };

  updateInterval = (interval) => {
    const { interval: oldInterval } = this.state;
    const { onChange, fixedRange } = this.props;
    let newInterval;
    if (interval.length === 1) {
      if (fixedRange === false) {
        newInterval = [interval[0], oldInterval[1]];
      } else {
        newInterval = [interval[0], moment(interval[0]).add(fixedRange, 'minutes')];
      }
    } else if (fixedRange === false) {
      newInterval = interval;
    } else {
      newInterval = [moment(interval[1]).subtract(fixedRange, 'minutes'), interval[1]];
    }
    onChange({ interval: newInterval, selectedShortcut: CUSTOM });
    this.setState({ interval: newInterval, selectedShortcut: CUSTOM });
  };

  onShortcutChange = (interval, selectedShortcut) => {
    const { onChange, durationAllowed } = this.props;
    onChange({
      interval,
      selectedShortcut,
    });
    if (interval) {
      const [from, to] = interval;
      if (moment(to).diff(from, 'months') <= durationAllowed) {
        this.setState({
          interval,
          selectedShortcut,
        });
        return;
      }
    }
    this.setState({ selectedShortcut });
  };

  // set end date based on start date
  // compare range should match main interval
  formatCompareInterval = (dates, fixedRange) => {
    const { interval: compareIntervalState } = this.state;
    const prevFrom = compareIntervalState?.[0] || dates[0];
    const newFrom = moment(dates[0].format()).hour(prevFrom.hour()).minute(prevFrom.minute());
    const newTo = moment(newFrom.format()).add(fixedRange, 'minutes');
    return [newFrom, newTo];
  };

  // both onChange and onCalendar change are triggerd for each click
  onCalendarChange = (dates) => {
    const { fixedRange, onChange } = this.props;
    if (fixedRange !== false) {
      const newInterval = this.formatCompareInterval(dates, fixedRange);
      onChange({ interval: newInterval, selectedShortcut: CUSTOM });
      this.setState({
        interval: newInterval,
      });
    }
  };

  render() {
    const {
      disabledDate,
      shortcuts,
      title,
      className,
      expanded,
      granularity,
      defaultValue,
      fixedRange,
      placement,
      live,
      liveMenuTooltip,
      showTimeInterval,
    } = this.props;

    const { interval, open, selectedShortcut } = this.state;
    return (
      <>
        {open && <ModalMask id={this.modalId} />}
        <div className={styles.datePicker}>
          <div ref={this.display}>
            <Display
              expanded={expanded}
              className={className}
              interval={interval}
              selected={open}
              title={title}
              shortcut={selectedShortcut}
            />
          </div>
          {open && (
            // eslint-disable-next-line react/jsx-no-duplicate-props
            <div className={classnames(styles.popup, { [styles.openLeft]: placement === 'left' })}>
              {shortcuts.length > 0 && (
                <Shortcuts
                  live={live}
                  liveMenuTooltip={liveMenuTooltip}
                  shortcuts={shortcuts}
                  onChange={this.onShortcutChange}
                  selected={selectedShortcut}
                />
              )}
              <div className={styles.main}>
                <Calendar
                  defaultValue={interval}
                  disabledDate={disabledDate}
                  onChange={this.updateDate}
                  onCalendarChange={this.onCalendarChange}
                  forceRender={fixedRange !== false}
                />
                <div className={styles.separator} />
                {showTimeInterval && (
                  <div className={styles.intervalSelector}>
                    <IntervalSelector
                      onChange={this.updateInterval}
                      interval={interval}
                      granularity={granularity}
                    />
                  </div>
                )}

                <Button
                  id="date-picker-apply"
                  className={styles.applyBtn}
                  type="primary"
                  onClick={this.onApply}
                  disabled={interval[0].isSameOrAfter(interval[1], 'minute')}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
}

DatePicker.defaultProps = {
  className: '',
  defaultValue: [],
  disabledDate: noop,
  shortcuts: [],
  defaultShortcut: CUSTOM,
  title: '',
  expanded: false,
  granularity: 5,
  onOpen: noop,
  onClose: noop,
  onApply: noop,
  onCancel: noop,
  onChange: noop,
  fixedRange: false,
  durationAllowed: 13, // months
  placement: '',
  showTimeInterval: true,
  live: false,
  liveMenuTooltip: undefined,
};

export default DatePicker;
